import type { TenantSubscriptionStatus } from '../services/api'
import { parseUTCDate } from './date'
import { plural } from './plural'

/**
 * The one banner every page shows for the tenant's plan state.
 *
 * Precedence: paused beats a running clock, which beats the trial offer, which
 * beats the subscribe prompt. A paid tenant in good standing gets nothing.
 * Decided from the subscription row alone — the endpoint count on it is the one
 * the compliance sweep kept, never a live count — so the banner and the API's
 * refusals cannot disagree.
 */
export interface SubscriptionBanner {
  /** Red stops service, yellow is a running clock, blue is an offer. */
  color: 'bg-red-600' | 'bg-yellow-600' | 'bg-blue-600'
  message: string
  /** Show the "Go to subscription" link. */
  linkToSubscription?: boolean
  /** A call to action the banner performs itself. */
  action?: 'start-trial'
}

export const SUSPENDED_MESSAGE = 'Your subscription has been suspended. Please contact support to renew.'
/** The trial's length when the backend has not said otherwise; the backend's own default. */
export const DEFAULT_TRIAL_DAYS = 14
export const trialOffer = (days: number): string =>
  `Need more endpoints and custom apps? Try the full product free for ${plural(days, 'day')}.`
export const SUBSCRIBE_PROMPT =
  'You are on the free plan. Subscribe for more endpoints and unlimited custom apps.'

const DAY_MS = 24 * 60 * 60 * 1000

// The backend's timestamps are UTC without a zone designator; `new Date` reads such a string as
// local time, which moves the deadline by the zone offset and rounds the count up west of UTC.
const daysUntil = (iso: string, now: number): number =>
  Math.max(0, Math.ceil((parseUTCDate(iso).getTime() - now) / DAY_MS))


export const subscriptionBannerFor = (
  s: TenantSubscriptionStatus | null | undefined,
  now: number = Date.now(),
): SubscriptionBanner | null => {
  if (!s?.has_subscription) return null

  // Paused means no service at all. The reason comes from the backend so this
  // banner and the API's refusals say the same thing; the portal stays readable
  // and the subscription page is the way back in.
  if (s.service_paused) {
    return { color: 'bg-red-600', message: s.pause_reason || SUSPENDED_MESSAGE, linkToSubscription: true }
  }
  if (s.status === 'suspended') return { color: 'bg-red-600', message: SUSPENDED_MESSAGE }
  if (s.status === 'cancelled') return { color: 'bg-red-600', message: 'Your subscription has been cancelled.' }

  // A running clock: the reason, both remedies, and one date. Whichever clock
  // ends first is the one that stops service.
  const clocks = [s.overage_deadline, s.payment_deadline].filter((d): d is string => !!d)
  if (clocks.length > 0) {
    const deadline = clocks.reduce((a, b) => (parseUTCDate(a) <= parseUTCDate(b) ? a : b))
    const left = plural(daysUntil(deadline, now), 'day')
    if (s.overage_deadline && s.plan === 'free') {
      const limit = (s.free_endpoint_limit ?? 100).toLocaleString()
      const message = s.endpoints_measured_at
        ? `${(s.endpoints_measured ?? 0).toLocaleString()} endpoints are in use, more than the free plan's ${limit}. ` +
          `Reduce to ${limit} endpoints or subscribe within ${left}, or packaging and deployment pause.`
        : `Your endpoint count could not be read. Recount it from the Subscription page or subscribe within ${left}, ` +
          'or packaging and deployment pause.'
      return { color: 'bg-yellow-600', message, linkToSubscription: true }
    }
    if (s.overage_deadline) {
      return {
        color: 'bg-yellow-600',
        message: `More endpoints are in use than your plan covers. Raise your endpoint count within ${left}.`,
        linkToSubscription: true,
      }
    }
    return {
      color: 'bg-yellow-600',
      message: `Your subscription has expired. ${left} left in the grace period.`,
      linkToSubscription: true,
    }
  }

  if (s.free_first) {
    if (s.plan === 'trial' && s.trial_ends_at) {
      return {
        color: 'bg-blue-600',
        message: `Your free trial ends in ${plural(daysUntil(s.trial_ends_at, now), 'day')}. You return to the free plan then unless you subscribe.`,
        linkToSubscription: true,
      }
    }
    if (s.plan === 'free' && s.trial_available) {
      return {
        color: 'bg-blue-600',
        message: trialOffer(s.trial_days ?? DEFAULT_TRIAL_DAYS),
        action: 'start-trial',
      }
    }
    if (s.plan === 'free') {
      return { color: 'bg-blue-600', message: SUBSCRIBE_PROMPT, linkToSubscription: true }
    }
    return null
  }

  // Legacy model: the trial started at sign-up and warns as it runs out.
  const days = s.days_until_expiry
  if (days !== null && days !== undefined && days <= 7 && days > 0) {
    return {
      color: 'bg-yellow-600',
      message: `Your subscription expires in ${plural(days, 'day')}. Please renew to avoid service interruption.`,
    }
  }
  if (days !== null && days !== undefined && days <= 0 && s.status === 'trial') {
    return { color: 'bg-red-600', message: 'Your trial has expired. Please upgrade to continue using all features.' }
  }
  return null
}
